/**
 * Logging Service
 *
 * Production-grade logging that sends logs to Cloud Logging via backend.
 * - Batches logs every 30 seconds to reduce network overhead
 * - Automatically captures context (URL, timestamp, user session)
 * - Falls back to console in development mode
 * - Queues failed logs for retry
 *
 * Usage:
 *   import { logger } from './loggingService';
 *   logger.info('User action', { action: 'click', target: 'button' });
 *   logger.error('Failed to load', { error: err.message });
 */
import { gatewayFetch } from './gateway';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  timestamp?: string;
  url?: string;
  sessionId?: string;
}

interface QueuedLog extends LogEntry {
  timestamp: string;
  url: string;
}

// Generate a unique session ID for this browser session
const generateSessionId = (): string => {
  const stored = sessionStorage.getItem('indexalpha_session_id');
  if (stored) return stored;

  const id = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  sessionStorage.setItem('indexalpha_session_id', id);
  return id;
};

class LoggingService {
  private queue: QueuedLog[] = [];
  private flushInterval: ReturnType<typeof setInterval> | null = null;
  private sessionId: string;
  private isProduction: boolean;
  private maxQueueSize = 100;
  private flushIntervalMs = 30000; // 30 seconds

  constructor() {
    this.sessionId = generateSessionId();
    this.isProduction = process.env.NODE_ENV === 'production';

    // No base URL here: the field was assigned and never read, and its
    // default baked the region and project id into every bundle for nothing.
    // Anything this service sends goes through the gateway client like the rest.

    // Start periodic flush in production
    if (this.isProduction && typeof window !== 'undefined') {
      this.flushInterval = setInterval(() => this.flush(), this.flushIntervalMs);

      // Flush on page unload
      window.addEventListener('beforeunload', () => {
        this.flush();
      });
    }
  }

  /**
   * Log an entry with specified level
   */
  log(entry: LogEntry): void {
    const queuedLog: QueuedLog = {
      ...entry,
      timestamp: new Date().toISOString(),
      url: typeof window !== 'undefined' ? window.location.pathname : '',
      sessionId: this.sessionId,
    };

    // Always log to console in development
    if (!this.isProduction) {
      const consoleMethod = entry.level === 'debug' ? 'log' : entry.level;
      console[consoleMethod](`[${entry.level.toUpperCase()}]`, entry.message, entry.context || '');
      return;
    }

    // Queue for backend in production
    this.queue.push(queuedLog);

    // Prevent queue from growing too large
    if (this.queue.length > this.maxQueueSize) {
      this.queue.shift(); // Remove oldest log
    }

    // Immediate flush for errors
    if (entry.level === 'error') {
      this.flush();
    }
  }

  /**
   * Who and what is reporting — attached to each flushed batch.
   *
   * **The uid, never an address.** An error report has to be traceable to a
   * person or it cannot be followed up, but this repo does not put addresses
   * into logs; the uid is the identifier the admin console already joins on.
   *
   * Resolved by a LAZY import so the logger keeps working — and keeps
   * loading — in contexts where Firebase is absent or not yet initialised.
   * Everything here is best effort: a logger that can throw is a logger that
   * turns one error into two.
   *
   * The uid is the signed-in user AT FLUSH TIME, which for an error is
   * effectively immediate (errors flush at once) and for buffered info lines
   * is within the flush interval. Worth knowing rather than assuming it is
   * per-line exact.
   */
  private async clientContext(): Promise<Record<string, string>> {
    const context: Record<string, string> = {};
    try {
      if (typeof navigator !== 'undefined' && navigator.userAgent) {
        context.userAgent = navigator.userAgent.slice(0, 300);
      }
      if (typeof window !== 'undefined' && window.innerWidth) {
        // Viewport rather than screen size: "it breaks on mobile" is about
        // the space the app was actually given, not the hardware.
        context.viewport = `${window.innerWidth}x${window.innerHeight}`;
      }
      const { auth } = await import('../firebase');
      const uid = auth?.currentUser?.uid;
      if (uid) context.uid = uid;
    } catch {
      // Best effort by design — see above.
    }
    return context;
  }

  /**
   * Flush queued logs to backend
   */
  async flush(): Promise<void> {
    if (this.queue.length === 0) return;

    const logs = [...this.queue];
    this.queue = [];

    try {
      const response = await gatewayFetch('logs', undefined, {
        auth: 'none',
        // The `client` block answers the three questions an error report is
        // useless without — WHICH browser, WHAT size of screen, and WHO
        // (owner, 2026-09-23: *"platform info, browser agents, timestamps,
        // and who"*). Sent ONCE per batch rather than on every line: a user
        // agent is ~120 characters and repeating it per entry would be most
        // of the payload.
        body: { logs, client: await this.clientContext() },
      });

      if (!response.ok) {
        // Re-queue on failure (but limit to prevent infinite growth)
        if (this.queue.length + logs.length <= this.maxQueueSize) {
          this.queue.unshift(...logs);
        }
      }
    } catch (error) {
      // Re-queue on network failure
      if (this.queue.length + logs.length <= this.maxQueueSize) {
        this.queue.unshift(...logs);
      }
      // Don't log the error to avoid infinite loop
      console.warn('[LoggingService] Failed to flush logs:', error);
    }
  }

  /**
   * Debug level - only shown in development
   */
  debug(message: string, context?: Record<string, unknown>): void {
    this.log({ level: 'debug', message, context });
  }

  /**
   * Info level - general operational messages
   */
  info(message: string, context?: Record<string, unknown>): void {
    this.log({ level: 'info', message, context });
  }

  /**
   * Warning level - potential issues that don't prevent operation
   */
  warn(message: string, context?: Record<string, unknown>): void {
    this.log({ level: 'warn', message, context });
  }

  /**
   * Error level - failures that need attention (flushes immediately)
   */
  error(message: string, context?: Record<string, unknown>): void {
    this.log({ level: 'error', message, context });
  }

  /**
   * Log CRUD operations with standardized format
   */
  logCrud(
    operation: 'create' | 'read' | 'update' | 'delete',
    entity: string,
    entityId: string,
    success: boolean,
    error?: string
  ): void {
    const level = success ? 'info' : 'error';
    const message = success
      ? `${entity} ${operation}d successfully`
      : `${entity} ${operation} failed`;

    this.log({
      level,
      message,
      context: {
        operation,
        entity,
        entityId,
        success,
        error,
      },
    });
  }

  /**
   * Stop the flush interval (for cleanup in tests)
   */
  destroy(): void {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
  }
}

// Singleton instance
export const logger = new LoggingService();

// Export class for testing
export { LoggingService };
